import { useCallback, useEffect, useState } from "react";
import { Show, SignInButton, useAuth } from "@clerk/react";
import { Trash2 } from "lucide-react";
import { apiFetch, fetchReel, saveBlob, resLabel } from "@/lib/reels";
import { Thumb } from "@/components/reels/BulkResults";
import { ProgressBar } from "@/components/reels/ProgressBar";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const HistoryRow = ({ item, i, onDelete, onSaved }) => {
  const { getToken } = useAuth();
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  const redownload = async () => {
    setError("");
    setProgress({ phase: "queued", progress: 0 });
    try {
      const r = await fetchReel(item.url, { quality: item.quality, token: await getToken(), onProgress: setProgress });
      saveBlob(r.blob, r.name);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setProgress(null);
    }
  };

  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`history-item-${i}`}>
      <Thumb src={item.thumb_data || item.thumbnail} className="h-[72px] w-[48px] shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-[14px] leading-snug" data-testid={`history-item-${i}-caption`}>{item.caption || item.url}</p>
        <p className="rs-secondary mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px]">
          <span>{item.platform === "tiktok" ? "TikTok" : "Instagram"}</span>
          {item.uploader && <span className="truncate">@{item.uploader}</span>}
          {resLabel(item) && <span className="rs-badge">{resLabel(item)}</span>}
          <span>{when(item.created_at)}</span>
        </p>
        {progress && <ProgressBar progress={progress} compact testId={`history-item-${i}-progress`} />}
        {error && <p className="text-[12px] text-[#c62d22]" data-testid={`history-item-${i}-error`}>{error}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <button className="rs-bordered text-[13px]" disabled={!!progress} onClick={redownload} data-testid={`history-item-${i}-redownload-btn`}>Save</button>
        <button className="rs-secondary p-1" onClick={() => onDelete(item.id)} aria-label="Remove" data-testid={`history-item-${i}-delete-btn`}><Trash2 size={15} /></button>
      </div>
    </li>
  );
};

const HistoryList = () => {
  const { getToken } = useAuth();
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async (before = "") => {
    setError("");
    try {
      const data = await (await apiFetch(`/history?before=${encodeURIComponent(before)}`, { token: await getToken() })).json();
      setItems((prev) => (before ? [...(prev || []), ...data.items] : data.items));
      setNext(data.next);
    } catch (e) {
      setError(e.message);
      setItems((prev) => prev || []);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    setError("");
    try {
      await apiFetch(`/history/${id}`, { method: "DELETE", token: await getToken() });
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };
  const clear = async () => {
    if (!window.confirm("Clear your whole download history?")) return;
    setError("");
    try {
      await apiFetch("/history", { method: "DELETE", token: await getToken() });
      setItems([]);
      setNext(null);
    } catch (e) {
      setError(e.message);
    }
  };

  if (!items) return <div className="flex justify-center py-6"><span className="rs-spinner" /></div>;
  return (
    <div className="rs-material p-4 rs-fade" data-testid="history-list">
      <div className="mb-1 flex items-center justify-between">
        <p className="rs-secondary text-[13px]" data-testid="history-count">{items.length} download{items.length === 1 ? "" : "s"}{next ? "+" : ""}</p>
        {items.length > 0 && <button className="text-[13px] text-[#c62d22]" onClick={clear} data-testid="history-clear-btn">Clear All</button>}
      </div>
      {error && <p className="text-[13px] text-[#c62d22]" data-testid="history-error">{error}</p>}
      {items.length === 0 ? (
        <p className="rs-secondary py-6 text-center text-[15px]" data-testid="history-empty">Videos you download while signed in show up here, on every device.</p>
      ) : (
        <ul className="divide-y divide-[#eadfcb]">{items.map((it, i) => <HistoryRow key={it.id} item={it} i={i} onDelete={remove} onSaved={() => load()} />)}</ul>
      )}
      {next && <button className="rs-bordered mt-3 w-full" onClick={() => load(next)} data-testid="history-load-more-btn">Load More</button>}
    </div>
  );
};

export const HistoryPanel = () => (
  <>
    <Show when="signed-out">
      <div className="rs-material flex flex-col items-center gap-3 p-6 text-center rs-fade" data-testid="history-signed-out">
        <p className="text-[17px] font-semibold">Your downloads, everywhere</p>
        <p className="rs-secondary text-[15px]">Sign in and every video you save is listed here on any device.</p>
        <SignInButton><button className="rs-prominent w-full" data-testid="history-sign-in-btn">Sign In</button></SignInButton>
      </div>
    </Show>
    <Show when="signed-in"><HistoryList /></Show>
  </>
);
